import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { sowConversationService, QuestionnaireQuestion, QuestionnaireAnswer } from '../../services/sowConversationService';
import { sowService } from '../../services/sowService';
import { SOW } from '../../models/sow';
import { Question } from '../../components/Question/Question';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Loader2, 
  AlertCircle,
  FileText,
  Building,
  Home,
  ChevronDown,
  ChevronUp,
  Menu
} from 'lucide-react';
import { useAuthToken } from '../../components/AuthTokenProvider';

interface SOWQuestionnairePageProps {}

export const SOWQuestionnairePage: React.FC<SOWQuestionnairePageProps> = () => {
  const { sowId } = useParams<{ sowId: string }>();
  const navigate = useNavigate();
  const { tokenSet, isLoading: tokenLoading } = useAuthToken();
  
  // SOW and questionnaire state
  const [sow, setSOW] = useState<SOW | null>(null);
  const [questions, setQuestions] = useState<QuestionnaireQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<string, QuestionnaireAnswer>>(new Map());
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [imageRefreshTrigger, setImageRefreshTrigger] = useState(0);

  // Load SOW and questionnaire data
  useEffect(() => {
    if (sowId && tokenSet && !tokenLoading) {
      loadData();
    }
  }, [sowId, tokenSet, tokenLoading]);

  const loadData = async () => {
    if (!sowId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load SOW data first to get project type
      const sowData = await sowService.getSOW(sowId);
      setSOW(sowData);
      
      // Check if SOW is in the right status for questionnaire
      if ((sowData.status !== 'initialQuestionnaire' && sowData.status !== 'estimating') || !sowData.scopeDefinition) {
        setError('This SOW is not ready for questionnaire. Please complete the initial setup first.');
        return;
      }
      
      // Load questionnaire
      const response = await sowConversationService.getQuestionnaire(sowId);
      const sortedQuestions = response.questions.sort((a, b) => a.priority - b.priority);
      setQuestions(sortedQuestions);
      
      // Load saved progress from SOW record
      if (sowData.questionnaireAnswers && sowData.questionnaireAnswers.length > 0) {
        const savedAnswersMap = new Map<string, QuestionnaireAnswer>();
        sowData.questionnaireAnswers.forEach((answer: any) => {
          savedAnswersMap.set(answer.questionId, {
            questionId: answer.questionId,
            answer: answer.answer,
            imageIds: answer.imageIds || []
          });
        });
        setAnswers(savedAnswersMap);
        
        // Set current question to the first unanswered question
        let nextQuestionIndex = 0;
        for (let i = 0; i < sortedQuestions.length; i++) {
          if (!savedAnswersMap.has(sortedQuestions[i].id)) {
            nextQuestionIndex = i;
            break;
          }
          // If we're on the last question and it's answered, stay on it
          if (i === sortedQuestions.length - 1) {
            nextQuestionIndex = i;
          }
        }
        setCurrentQuestionIndex(nextQuestionIndex);
      }
      
    } catch (error) {
      console.error('Error loading questionnaire:', error);
      setError('Failed to load questionnaire. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  const saveProgress = async (updatedAnswers: Map<string, QuestionnaireAnswer>) => {
    if (!sowId) return;
    
    try {
      setSaving(true);
      const answerArray: QuestionnaireAnswer[] = Array.from(updatedAnswers.values());
      
      await sowConversationService.saveQuestionnaireProgress(sowId, answerArray);
    } catch (error) {
      console.error('Error saving progress:', error);
      // Don't show error to user for progress saves, just log it
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async (response: string, imageIds?: string[]) => {
    if (!currentQuestion) return;
    
    try {
      const newAnswers = new Map(answers);
      newAnswers.set(currentQuestion.id, {
        questionId: currentQuestion.id,
        answer: response,
        imageIds: imageIds || []
      });
      setAnswers(newAnswers);
      
      // Save progress automatically
      await saveProgress(newAnswers);
      
      // Move to next question or complete
      if (isLastQuestion) {
        submitQuestionnaire();
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    } catch (error) {
      console.error('Error handling continue:', error);
      setError('Failed to save progress. Please try again.');
    }
  };

  const handlePreviousQuestion = () => {
    if (!isFirstQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      submitQuestionnaire();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const submitQuestionnaire = async () => {
    if (!sowId) return;
    
    try {
      setSubmitting(true);
      
      const answerArray: QuestionnaireAnswer[] = Array.from(answers.values());

      await sowConversationService.submitQuestionnaire(sowId, answerArray);
      
      setIsComplete(true);
      
      // Redirect to overview page after a short delay
      setTimeout(() => {
        navigate(`/sow/${sowId}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      setError('Failed to submit questionnaire. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToOverview = () => {
    navigate(`/sow/${sowId}`);
  };

  const handleGoToQuestion = (questionIndex: number) => {
    setCurrentQuestionIndex(questionIndex);
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    if (!sowId) {
      throw new Error('SOW ID not found');
    }
    
    try {
      const imageId = await sowService.uploadImageForAnalysis(sowId, file);
      console.log('Image uploaded with ID:', imageId);
      return imageId;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleImageUploaded = () => {
    setImageRefreshTrigger(prev => prev + 1);
  };

  const handleImageAssessment = async (imageId: string, questionId: string, attemptNumber: number) => {
    if (!sowId || !currentQuestion) {
      throw new Error('Missing SOW ID or current question');
    }

    try {
      const assessment = await sowService.assessImageQuality(
        sowId, 
        imageId, 
        questionId, 
        currentQuestion.question, 
        attemptNumber
      );
      
      console.log('Image assessment result:', assessment);
      return assessment;
    } catch (error) {
      console.error('Error assessing image:', error);
      // Return a fallback assessment to avoid blocking the user
      return {
        status: 'accepted',
        confidence: 0.6,
        feedback: 'Unable to assess image quality, but proceeding with estimate.',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  };


  // Loading state
  if (loading || tokenLoading || !tokenSet) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <div className="text-lg">Loading questionnaire...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Unable to Load Questionnaire</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-3">
              <Button onClick={handleBackToOverview}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Overview
              </Button>
              <Button onClick={loadData} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state
  if (isComplete) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Questionnaire Complete!</h3>
            <p className="text-gray-600 mb-4">
              Thank you for providing detailed information about your {sow?.scopeDefinition?.projectType.toLowerCase()} renovation.
            </p>
            <div className="flex items-center justify-center mb-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span>Generating work items and redirecting...</span>
            </div>
            <Button onClick={handleBackToOverview} variant="outline">
              Return to Overview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No questions available
  if (!currentQuestion || questions.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Questions Available</h3>
            <p className="text-gray-600 mb-6">No questions are available for this project type.</p>
            <Button onClick={handleBackToOverview}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Overview
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={handleBackToOverview}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Overview
        </Button>
        
        <div className="flex items-center gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold">Project Questionnaire</h1>
            <div className="flex items-center gap-2 text-gray-600">
              <Building className="h-4 w-4" />
              <span>{sow?.scopeDefinition?.projectType}</span>
              <span>•</span>
              <Home className="h-4 w-4" />
              <span>{sow?.address}</span>
            </div>
          </div>
        </div>

        {/* Slim Progress Overview */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-blue-600">{currentQuestionIndex + 1}</span>
              <span className="text-gray-500">of</span>
              <span className="text-sm font-medium text-gray-700">{questions.length}</span>
              <span className="text-sm text-gray-600">questions</span>
              {saving && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Saving...</span>
                </div>
              )}
            </div>
            
            {/* Progress Toggle */}
            <Button
              variant="outline" 
              size="sm"
              onClick={() => setIsProgressOpen(!isProgressOpen)}
              className="flex items-center gap-1 h-8 px-3 text-xs"
            >
              <Menu className="h-3 w-3" />
              Progress
              {isProgressOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
          
          {/* Slim Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500 ease-out" 
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Progress List (Collapsible) */}
        {isProgressOpen && (
          <div className="bg-white border rounded-lg shadow-sm mt-4">
            <div className="p-3 max-h-80 overflow-y-auto">
              <div className="grid gap-1">
                {questions.map((question, index) => {
                  const isAnswered = answers.has(question.id);
                  const isCurrent = index === currentQuestionIndex;
                  const isAccessible = index <= currentQuestionIndex || isAnswered;
                  
                  return (
                    <button
                      key={question.id}
                      onClick={() => {
                        if (isAccessible) {
                          handleGoToQuestion(index);
                          setIsProgressOpen(false);
                        }
                      }}
                      disabled={!isAccessible}
                      className={`w-full text-left p-3 rounded-md transition-all ${
                        isCurrent 
                          ? 'bg-blue-100 text-blue-900'
                          : isAnswered
                            ? 'bg-green-50 text-green-800 hover:bg-green-100'
                            : isAccessible
                              ? 'hover:bg-gray-50 text-gray-700'
                              : 'text-gray-400 cursor-not-allowed opacity-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">Q{index + 1}</span>
                            {isAnswered && <CheckCircle className="h-4 w-4 text-green-600" />}
                            {isCurrent && !isAnswered && <div className="h-2 w-2 bg-blue-500 rounded-full" />}
                          </div>
                          <div className="text-xs text-gray-600 line-clamp-2">
                            {question.question}
                          </div>
                          {isAnswered && answers.get(question.id) && (
                            <div className="text-xs mt-1 text-green-700 font-medium">
                              {answers.get(question.id)?.answer}
                              {(() => {
                                const answer = answers.get(question.id);
                                const imageIds = answer?.imageIds;
                                return imageIds && imageIds.length > 0 && (
                                  <span className="ml-2 text-blue-600">📸 {imageIds.length} image(s)</span>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Question Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline">{currentQuestion?.category}</Badge>
            <Badge variant="outline">Question {currentQuestionIndex + 1}</Badge>
          </div>
          {currentQuestion?.question && (
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {currentQuestion.question}
            </h2>
          )}
        </div>

        {/* Question Component */}
        {currentQuestion && (
          <Question
            question={currentQuestion}
            onContinue={handleContinue}
            onImageUpload={handleImageUpload}
            onImageUploaded={handleImageUploaded}
            onImageAssessment={handleImageAssessment}
            isSubmitting={submitting}
          />
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePreviousQuestion}
            disabled={isFirstQuestion || submitting}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="text-sm text-gray-500 flex items-center">
            {saving && (
              <div className="flex items-center gap-2 mr-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {isLastQuestion ? 'Complete questionnaire to finish' : 'Continue to next question'}
          </div>
        </div>
      </div>
    </div>
  );
};